import React from "react";
import { Link, useLocation } from "react-router-dom";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { LEGAL_PAGES } from "@/constants/legal";

// ---- Reusable content primitives for legal pages ----
export const Section = ({ title, children }) => (
  <section className="space-y-3 scroll-mt-24">
    <h2 className="font-display text-xl font-semibold text-foreground">{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
  </section>
);

export const Bullets = ({ items }) => (
  <ul className="ml-1 space-y-2">
    {items.map((it, i) => (
      <li key={`b-${i}`} className="flex gap-2.5">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" aria-hidden="true" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

// ---- Shared layout: marketing nav + sticky legal index + content + footer ----
export const LegalLayout = ({ slug, title, subtitle, lastUpdated = true, children }) => {
  const location = useLocation();
  const updated = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:py-16" data-testid={`${slug}-page`}>
        <div className="grid gap-10 lg:grid-cols-[240px_1fr]">
          {/* Sidebar index */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Legal &amp; compliance</p>
              <nav className="mt-3 space-y-0.5">
                {LEGAL_PAGES.map((p) => {
                  const active = location.pathname === p.path;
                  return (
                    <Link
                      key={p.slug}
                      to={p.path}
                      data-testid={`legal-sidebar-${p.slug}`}
                      className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
                        active
                          ? "bg-accent font-medium text-foreground"
                          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                      }`}
                    >
                      {p.title}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article>
            <p className="text-sm font-medium text-primary">Legal &amp; compliance</p>
            <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight">{title}</h1>
            {subtitle && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{subtitle}</p>}
            {lastUpdated && (
              <p className="mt-3 text-xs text-muted-foreground">Last updated: {updated}</p>
            )}
            <div className="mt-10 max-w-3xl space-y-10">{children}</div>
          </article>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
};
